import { useState } from "react";
import TriStateControl from "./TriStateControl";

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const comma = text.indexOf(",");
      resolve(comma >= 0 ? text.slice(comma + 1) : text);
    };
    reader.onerror = () => reject(reader.error || new Error("Datei konnte nicht gelesen werden"));
    reader.readAsDataURL(file);
  });
}

export function Pkcs12Editor({ fields, onChange, disabled }) {
  const [reading, setReading] = useState(false);
  const hasCert = Boolean(fields?.PayloadContent);

  const patch = (partial) => onChange({ ...fields, ...partial });

  return (
    <div className="row g-3">
      <div className="col-12">
        {hasCert ? (
          <div className="border rounded p-3">
            <div className="fw-semibold mb-1">Zertifikat: {fields.PayloadCertificateFileName || "PKCS#12"}</div>
            <div className="small text-muted">Identität in diesem Profil. WLAN-Enterprise kann dieses Zertifikat über die UUID zuweisen.</div>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm mt-2"
              disabled={disabled}
              onClick={() => patch({ PayloadContent: undefined, PayloadCertificateFileName: undefined, Password: undefined })}
            >
              Dieses Zertifikat entfernen
            </button>
          </div>
        ) : (
          <div>
            <label className="form-label">Neues Zertifikat hochladen</label>
            <input
              type="file"
              className="form-control"
              disabled={disabled || reading}
              accept=".p12,.pfx,application/x-pkcs12"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                setReading(true);
                try {
                  const content = await readFileAsBase64(file);
                  patch({
                    PayloadContent: content,
                    PayloadCertificateFileName: file.name,
                  });
                } finally {
                  setReading(false);
                }
              }}
            />
          </div>
        )}
      </div>
      <div className="col-md-6">
        <label className="form-label">Passwort</label>
        <input
          type="password"
          className="form-control"
          disabled={disabled}
          value={fields?.Password || ""}
          onChange={(e) => patch({ Password: e.target.value || undefined })}
        />
        <div className="form-text">Wird im Profil hinterlegt. Lieber über MDM als per Datei verteilen.</div>
      </div>
      <div className="col-12">
        <div className="profile-field-row mb-2">
          <span className="profile-field-label" title="Gilt beim Hochladen eines PKCS#12-Zertifikats (macOS).">
            Administrator das Exportieren des privaten Schlüssels erlauben
          </span>
          <TriStateControl
            value={fields?.KeyIsExtractable}
            onChange={(value) => patch({ KeyIsExtractable: value })}
            disabled={disabled}
            name="Schlüssel extrahierbar"
          />
        </div>
        <div className="profile-field-row">
          <span className="profile-field-label" title="Gilt beim Hochladen eines PKCS#12-Zertifikats (macOS).">
            Allen Apps den Zugriff auf den privaten Schlüssel erlauben
          </span>
          <TriStateControl
            value={fields?.AllowAllAppsAccess}
            onChange={(value) => patch({ AllowAllAppsAccess: value })}
            disabled={disabled}
            name="Schlüssel zugreifbar"
          />
        </div>
      </div>
    </div>
  );
}

export function RootCertificateEditor({ fields, onChange, disabled }) {
  const [reading, setReading] = useState(false);
  const patch = (partial) => onChange({ ...fields, ...partial });

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Dateiname</label>
        <input
          className="form-control"
          disabled={disabled}
          value={fields?.PayloadCertificateFileName || ""}
          onChange={(e) => patch({ PayloadCertificateFileName: e.target.value || undefined })}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Datei (PEM oder DER)</label>
        <input
          type="file"
          className="form-control"
          disabled={disabled || reading}
          accept=".pem,.cer,.crt,.der,application/x-x509-ca-cert,application/pkix-cert"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            setReading(true);
            try {
              const content = await readFileAsBase64(file);
              patch({
                PayloadContent: content,
                PayloadCertificateFileName: fields?.PayloadCertificateFileName || file.name,
              });
            } finally {
              setReading(false);
            }
          }}
        />
      </div>
      <div className="col-12">
        <label className="form-label">Zertifikat (PEM oder Base64-DER)</label>
        <textarea
          className="form-control font-monospace small"
          rows={6}
          disabled={disabled}
          value={fields?.PayloadContent || ""}
          onChange={(e) => patch({ PayloadContent: e.target.value || undefined })}
        />
      </div>
    </div>
  );
}
